import { useMemo } from 'react'
import { deadlineKey, deadlineDaysLeft, deadlineDateKey, daysBetweenKeys, isOverdue, parseDateKey, type Task } from '@trackmind/core'
import { useApp } from '@/context/AppContext'
import { useToday } from '@/hooks/useToday'

/**
 * Deadlines, read the one way core reads them, in the user's time zone.
 *
 * Every component used to read `task.deadline` itself — `new Date(deadline)`
 * here, `deadline.split('T')[0]` there, `Math.ceil` against the current
 * instant somewhere else — and for quick-captured deadlines (stored as UTC
 * instants) those disagreed by a day: the card showed the 5th while Today and
 * "overdue" worked from the 6th. Task Finder flagged anything due today as
 * overdue from 00:01. These are the only accessors the UI should use.
 */
export function useDeadlines() {
  const { preferences } = useApp()
  const zone = preferences?.timezone
  const todayKey = useToday()
  return useMemo(() => ({
    /** The local `YYYY-MM-DD` the task is due, or null. */
    key: (task: Task) => deadlineKey(task, zone),
    /** Days until it's due from today; negative once overdue, null without one. */
    daysLeft: (task: Task) => deadlineDaysLeft(task, todayKey(), zone),
    /** Past its deadline and still counts as open work (never a habit). */
    overdue: (task: Task) => isOverdue(task, todayKey(), zone),
    /** The due day as a local Date, for display. */
    date: (task: Task) => {
      const key = deadlineKey(task, zone)
      return key ? parseDateKey(key) : null
    },
    /** For a stored deadline string that isn't on a Task (e.g. a review). */
    keyOf: (deadline: string | null | undefined) => deadlineDateKey(deadline, zone),
    /** Days left for such a string. */
    daysLeftOf: (deadline: string | null | undefined) => {
      const key = deadlineDateKey(deadline, zone)
      return key ? daysBetweenKeys(todayKey(), key) : null
    },
  }), [zone, todayKey])
}
